/**
 * Site-wide SEO defaults (the "site-wide defaults store" from the SEO spec).
 *
 * These are the per-flavor brand facts used to build the site-wide
 * Organization / WebSite JSON-LD and to supply default Open Graph values when a
 * page/content record omits its own. They are intentionally static/build-time
 * (brand identity does not change at runtime); the backend SEO override store
 * (documented in `docs/seo-structured-data.md`) layers on top per page.
 *
 * `siteUrl` prefers the explicit `REACT_APP_SITE_URL` build var and falls back
 * to the live `window.location.origin` so canonical/sitemap URLs are correct in
 * every environment without hard-coding a host.
 */
import { CmsFlavor } from "@interfaces";

export interface SiteMeta {
    /** Brand / organisation display name. */
    name: string;
    /** Public site origin (no trailing slash). */
    siteUrl: string;
    /** Absolute (or root-relative) logo URL used for Organization JSON-LD. */
    logoUrl: string;
    /** Default Open Graph / Twitter image when a page supplies none. */
    defaultOgImage: string;
    /** Twitter/X handle (with leading @) for twitter:site, if any. */
    twitterHandle?: string;
    /** Social / authoritative profile URLs for Organization.sameAs. */
    sameAs: string[];
}

/** Resolve the active brand flavor from the build-time env. */
export const getFlavor = (): CmsFlavor => {
    const flavor = process.env.REACT_APP_FLAVOR;
    return flavor === "peterpans" ? "peterpans" : "freelance";
};

/** Live origin, safe in non-browser (test/SSR) contexts. */
const runtimeOrigin = (): string =>
    typeof window !== "undefined" && window.location ? window.location.origin : "";

/** Trim a trailing slash from an origin/URL. */
const trimTrailingSlash = (url: string): string => url.replace(/\/$/, "");

const BRANDS: Record<CmsFlavor, Omit<SiteMeta, "siteUrl">> = {
    freelance: {
        name: "Freelance Travel",
        logoUrl: "/images/favicon/logo_512.png",
        defaultOgImage: "/images/favicon/logo_512.png",
        twitterHandle: undefined,
        sameAs: [],
    },
    peterpans: {
        name: "Peterpans Adventure Travel",
        logoUrl: "/images/favicon/logo_512.png",
        defaultOgImage: "/images/favicon/logo_512.png",
        twitterHandle: "@peterpans",
        sameAs: [
            "https://www.facebook.com/peterpans",
            "https://www.instagram.com/peterpansadventuretravel",
        ],
    },
};

/**
 * Resolve the effective site metadata for the active (or given) flavor.
 * `siteUrl` = REACT_APP_SITE_URL || window.location.origin.
 */
export const getSiteMeta = (flavor: CmsFlavor = getFlavor()): SiteMeta => {
    const brand = BRANDS[flavor] ?? BRANDS.freelance;
    const configured = process.env.REACT_APP_SITE_URL;
    const siteUrl = trimTrailingSlash(configured || runtimeOrigin());
    return { ...brand, siteUrl };
};

/** Turn a root-relative asset path into an absolute URL against the site origin. */
export const absoluteUrl = (pathOrUrl: string | undefined, siteUrl: string): string | undefined => {
    if (!pathOrUrl) return undefined;
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const base = trimTrailingSlash(siteUrl);
    if (!base) return pathOrUrl;
    return `${base}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
};
